import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Trash2, Pencil, GripVertical } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/shared/empty-state'
import { supabase } from '@/lib/supabase/client'

function parseTitle(v: unknown): string {
  if (typeof v === 'string') return v
  if (v && typeof v === 'object') return (v as Record<string, string>).fa ?? ''
  return ''
}

interface SearchableField {
  id: string
  source_form_id: string
  source_field_key: string
  display_label: string
  is_displayed_in_list: boolean
  display_order: number
}

interface Form {
  id: string
  title: string
  current_version_id: string | null
}

async function fetchSearchableConfig(projectId: string) {
  const [configRes, formsRes] = await Promise.all([
    supabase
      .from('searchable_fields_config')
      .select('*')
      .eq('project_id', projectId)
      .order('display_order'),
    supabase
      .from('forms')
      .select('id, title, current_version_id')
      .eq('project_id', projectId)
      .eq('is_deleted', false)
      .eq('is_published', true),
  ])
  if (configRes.error) throw configRes.error
  return {
    fields: (configRes.data ?? []).map((c) => ({
      id: c.id,
      source_form_id: c.source_form_id,
      source_field_key: c.source_field_key,
      display_label: parseTitle(c.display_label),
      is_displayed_in_list: c.is_displayed_in_list,
      display_order: c.display_order,
    })) as SearchableField[],
    forms: (formsRes.data ?? []).map((f) => ({
      id: f.id,
      title: parseTitle(f.title),
      current_version_id: f.current_version_id,
    })) as Form[],
  }
}

async function fetchFormFields(versionId: string) {
  const { data } = await supabase
    .from('form_versions')
    .select('definition_snapshot')
    .eq('id', versionId)
    .single()
  if (!data) return []
  const def = data.definition_snapshot as { fields?: { field_key: string; label: { fa: string } | string; field_type: string }[]; steps?: { fields?: { key: string; label: string; type: string }[] }[] }
  if (Array.isArray(def.fields)) {
    return def.fields.map((f) => ({ key: f.field_key, label: typeof f.label === 'string' ? f.label : f.label?.fa || f.field_key, type: f.field_type }))
  }
  return (def.steps ?? []).flatMap((s) => s.fields ?? [])
}

interface SearchableFieldModalProps {
  open: boolean
  onClose: () => void
  forms: Form[]
  existing?: SearchableField | null
  onSave: (d: Omit<SearchableField, 'id' | 'display_order'>) => void
  saving: boolean
}

function SearchableFieldModal({ open, onClose, forms, existing, onSave, saving }: SearchableFieldModalProps) {
  const [label, setLabel] = useState('')
  const [formId, setFormId] = useState('')
  const [fieldKey, setFieldKey] = useState('')
  const [isInList, setIsInList] = useState(false)
  const [formFields, setFormFields] = useState<{ key: string; label: string; type: string }[]>([])

  useEffect(() => {
    if (existing) {
      setLabel(existing.display_label)
      setFormId(existing.source_form_id)
      setFieldKey(existing.source_field_key)
      setIsInList(existing.is_displayed_in_list)
    } else {
      setLabel(''); setFormId(''); setFieldKey(''); setIsInList(false)
    }
  }, [existing, open])

  useEffect(() => {
    if (!formId) { setFormFields([]); return }
    const form = forms.find((f) => f.id === formId)
    if (form?.current_version_id) {
      fetchFormFields(form.current_version_id).then(setFormFields)
    }
  }, [formId, forms])

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{existing ? 'ویرایش فیلد جستجو' : 'افزودن فیلد جستجو'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label>برچسب نمایشی</Label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="مثال: کد ملی" />
          </div>
          <div className="space-y-1.5">
            <Label>فرم منبع</Label>
            <Select value={formId} onValueChange={setFormId}>
              <SelectTrigger><SelectValue placeholder="انتخاب فرم..." /></SelectTrigger>
              <SelectContent>
                {forms.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {formId && (
            <div className="space-y-1.5">
              <Label>فیلد</Label>
              <Select value={fieldKey} onValueChange={setFieldKey}>
                <SelectTrigger><SelectValue placeholder="انتخاب فیلد..." /></SelectTrigger>
                <SelectContent>
                  {formFields.map((f) => (
                    <SelectItem key={f.key} value={f.key}>
                      {f.label || f.key}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex items-center gap-2">
            <Checkbox
              id="in_list"
              checked={isInList}
              onCheckedChange={(v) => setIsInList(Boolean(v))}
            />
            <label htmlFor="in_list" className="text-sm cursor-pointer">نمایش به عنوان ستون در جدول سوابق</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>انصراف</Button>
          <Button
            onClick={() => onSave({ source_form_id: formId, source_field_key: fieldKey, display_label: label, is_displayed_in_list: isInList })}
            disabled={saving || !label || !formId || !fieldKey}
          >
            {saving ? 'در حال ذخیره...' : 'ذخیره'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function SearchableFieldsContent() {
  const { projectId } = useParams<{ projectId: string }>()
  const qc = useQueryClient()
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingField, setEditingField] = useState<SearchableField | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['searchable-fields-config', projectId],
    queryFn: () => fetchSearchableConfig(projectId!),
    enabled: !!projectId,
  })

  const saveMutation = useMutation({
    mutationFn: async (payload: Omit<SearchableField, 'id' | 'display_order'>) => {
      if (editingField) {
        const { error } = await supabase
          .from('searchable_fields_config')
          .update({
            display_label: { fa: payload.display_label },
            is_displayed_in_list: payload.is_displayed_in_list,
          })
          .eq('id', editingField.id)
        if (error) throw error
      } else {
        const displayOrder = (data?.fields.length ?? 0) + 1
        const { error } = await supabase
          .from('searchable_fields_config')
          .insert({
            project_id: projectId!,
            source_form_id: payload.source_form_id,
            source_field_key: payload.source_field_key,
            display_label: { fa: payload.display_label },
            is_displayed_in_list: payload.is_displayed_in_list,
            display_order: displayOrder,
          })
        if (error) throw error
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['searchable-fields-config', projectId] })
      setEditorOpen(false)
      setEditingField(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) =>
      supabase.from('searchable_fields_config').delete().eq('id', id)
        .then(({ error }) => { if (error) throw error }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['searchable-fields-config', projectId] }),
  })

  const fields = data?.fields ?? []
  const forms = data?.forms ?? []

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-muted-foreground">
          فیلدهای قابل جستجو در سوابق و ستون‌های جدول را تعریف کنید.
        </p>
        <Button size="sm" onClick={() => { setEditingField(null); setEditorOpen(true) }} className="gap-1.5 shrink-0">
          <Plus className="w-4 h-4" />
          افزودن فیلد
        </Button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-14 bg-muted rounded-xl animate-pulse" />
          ))}
        </div>
      )}

      {!isLoading && fields.length === 0 && (
        <EmptyState
          icon={Plus}
          title="فیلد جستجویی تعریف نشده"
          description="فیلدهای قابل جستجو در سوابق را اضافه کنید"
          action={
            <Button size="sm" onClick={() => setEditorOpen(true)}>
              <Plus className="w-4 h-4 me-1.5" />
              افزودن فیلد
            </Button>
          }
        />
      )}

      {fields.length > 0 && (
        <div className="bg-card border border-border rounded-xl divide-y divide-border">
          {fields.map((field) => {
            const sourceForm = forms.find((f) => f.id === field.source_form_id)
            return (
              <div key={field.id} className="flex items-center gap-3 px-4 py-3">
                <GripVertical className="w-4 h-4 text-muted-foreground shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-foreground truncate">{field.display_label}</p>
                    {field.is_displayed_in_list && (
                      <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">ستون جدول</span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {sourceForm?.title ?? '—'} · <span className="font-mono">{field.source_field_key}</span>
                  </p>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button
                    size="sm" variant="ghost" className="h-7 w-7 p-0"
                    onClick={() => { setEditingField(field); setEditorOpen(true) }}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button
                    size="sm" variant="ghost" className="h-7 w-7 p-0 text-destructive hover:text-destructive"
                    onClick={() => deleteMutation.mutate(field.id)}
                    disabled={deleteMutation.isPending}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            )
          })}
        </div>
      )}

      <SearchableFieldModal
        open={editorOpen}
        onClose={() => { setEditorOpen(false); setEditingField(null) }}
        forms={forms}
        existing={editingField}
        onSave={(d) => saveMutation.mutate(d)}
        saving={saveMutation.isPending}
      />
    </>
  )
}

export function SearchableFieldsPage() {
  return (
    <div className="flex flex-col h-full">
      <PageHeader title="فیلدهای جستجو" breadcrumbs={[{ label: 'فیلدهای جستجو' }]} />
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl">
          <SearchableFieldsContent />
        </div>
      </div>
    </div>
  )
}
